/**
 * Business-metrics data hooks.
 *
 * `useBusinessSnapshot` fetches `/api/business/overview` (BusinessOverviewResponse)
 * and maps it to the BusinessSnapshot view-model that BusinessPage renders.
 * `useOrgs` / `useFacilities` fetch the directory endpoints and unwrap the
 * paged response so callers receive plain arrays.
 *
 * Demo mode (VITE_DEMO_MODE=1 or ?demo=1) short-circuits the network calls
 * and returns seeded deterministic data — useful for offline dev and design
 * reviews.
 */

import { useQuery } from '@tanstack/react-query';

import { HubApiError, hubFetch } from '@/api/client';
import type {
  BusinessOverviewResponse,
  BusinessTrendPoint,
  FacilityDetail,
  FacilityRow,
  OrgUser,
  OrganizationDetail,
  OrganizationRow,
  PagedResult,
  UserRow,
} from '@/api/types';
import { useEnvStore } from '@/env/envStore';
import type { Series } from '@/design-system/charts/types';
import {
  demoBusinessSnapshot,
  demoFacilities,
  demoFacility,
  demoOrg,
  demoOrgs,
  demoUsers,
  isDemoModeRequested,
} from '@/lib/demoData';
import type { DirectoryUserWithRoles } from '@/models';
import type { BusinessKpi, BusinessSnapshot } from '@/models/business';

import { demoFacilityUsers, demoOrgUsers } from './demoData';

// ---------------------------------------------------------------------------
// Mapping — BusinessOverviewResponse → BusinessSnapshot view-model
// ---------------------------------------------------------------------------

function trendToSeries(name: string, points: BusinessTrendPoint[]): Series {
  return { name, points: points.map((p) => ({ t: p.atMs, v: p.value })) };
}

function toSnapshot(r: BusinessOverviewResponse): BusinessSnapshot {
  const { latest, trend } = r;

  const kpis: BusinessKpi[] = [
    {
      key: 'organizations',
      label: 'Organizations',
      value: latest.orgs.total,
      unit: 'count',
    },
    {
      key: 'facilities',
      label: 'Facilities',
      value: latest.facilities.total,
      unit: 'count',
    },
    {
      key: 'activeUsers',
      label: 'Active users',
      value: latest.users.active,
      unit: 'count',
    },
    // patients deferred to M3.x (FHIR-backed); backend returns null — omit tile
    ...(latest.patients.active30d !== null
      ? ([
          {
            key: 'patients',
            label: 'Active patients',
            value: latest.patients.active30d,
            unit: 'count',
          } satisfies BusinessKpi,
        ] as const)
      : []),
    {
      key: 'documents',
      // signedThisMonth is a calendar MTD count, not a rolling 30-day window.
      label: 'Docs signed (MTD)',
      value: latest.docs.signedThisMonth,
      unit: 'count',
    },
    {
      key: 'dau',
      label: 'DAU',
      value: latest.dauToday,
      unit: 'count',
    },
    {
      key: 'mau',
      label: 'MAU',
      value: latest.mauToday,
      unit: 'count',
    },
  ];

  // DAU/MAU ratio: align dau and mau trend arrays by atMs, compute ratio where
  // both points exist and mau > 0 (avoids divide-by-zero on newly-seeded envs).
  const dauByMs = new Map(trend.dau.map((p) => [p.atMs, p.value]));
  const activityRatioPoints = trend.mau
    .filter((p) => p.value > 0 && dauByMs.has(p.atMs))
    .map((p) => ({ t: p.atMs, v: (dauByMs.get(p.atMs) ?? 0) / p.value }));

  return {
    asOf: r.asOfMs,
    kpis,
    signedDocsByDay: trendToSeries('Signed docs', trend.docsSigned),
    activityRatio: { name: 'DAU/MAU', points: activityRatioPoints },
  };
}

// ---------------------------------------------------------------------------
// Directory helpers
// ---------------------------------------------------------------------------

/** Walk pages of a paged directory endpoint up to `maxItems` total.
 *  Each request is sized to the remaining budget so the response is never
 *  larger than needed, and `r.items` is sliced before accumulating so the
 *  returned array never exceeds `maxItems` even on the last page.
 *  Returns `{ items, isCapped: true }` only when more data exists on the
 *  server beyond the cap — i.e. when we hit maxItems AND the last page had
 *  a non-null continuation token. Exactly maxItems records total (with no
 *  continuation) correctly produces isCapped: false.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
async function fetchAllPages<T>(
  path: string,
  params?: Record<string, string>,
  maxItems = 1_000,
): Promise<{ items: T[]; isCapped: boolean }> {
  const allItems: T[] = [];
  let continuation: string | null = null;
  let hitCapWithMore = false;

  do {
    const remaining = maxItems - allItems.length;
    // Cast to a plain structural type so ESLint's no-unsafe-* rules don't flag
    // the Omit<…> & {items: T[]} intersection that PagedResult<T> expands to.
    const r = await hubFetch<PagedResult<T>>(path, {
      searchParams: {
        ...params,
        take: Math.min(remaining, 200),
        ...(continuation ? { continuation } : {}),
      },
    }) as { items: T[]; continuation: string | null };
    allItems.push(...r.items.slice(0, remaining));
    if (allItems.length >= maxItems && r.continuation != null) {
      // Hit the cap and the server has more pages — record truncation.
      hitCapWithMore = true;
      continuation = null;
    } else {
      continuation = r.continuation ?? null;
    }
  } while (continuation !== null);

  return { items: allItems, isCapped: hitCapWithMore };
}



function isMissingUsersEndpoint(error: unknown): error is HubApiError {
  return error instanceof HubApiError && (error.status === 404 || error.status === 501);
}

type UsersQueryResult<T> = PagedResult<T> & {
  isCapped: boolean;
  endpointUnavailable: boolean;
};

type OrgUserWithEmailAddress = Omit<OrgUser, 'email'> & Pick<UserRow, 'emailAddress'>;
type DirectoryApiUser = OrgUser | OrgUserWithEmailAddress;

function emptyPagedResult<T>(endpointUnavailable = false): UsersQueryResult<T> {
  return { items: [], continuation: null, isCapped: false, endpointUnavailable };
}

async function fetchUsersWithFallback<T>(
  path: string,
  params?: Record<string, string>,
): Promise<UsersQueryResult<T>> {
  try {
    const result = await fetchAllPages<T>(path, params);
    return { items: result.items, continuation: null, isCapped: result.isCapped, endpointUnavailable: false };
  } catch (error) {
    if (isMissingUsersEndpoint(error)) {
      return emptyPagedResult<T>(true);
    }
    throw error;
  }
}

export function toDirectoryUserWithRoles(user: DirectoryApiUser): DirectoryUserWithRoles {
  const emailAddress = 'emailAddress' in user ? user.emailAddress : user.email;
  return {
    userId: user.userId,
    displayName: user.displayName,
    emailAddress,
    status: user.status,
    roles: user.roles,
    assignedFacilities: user.assignedFacilities,
    roleReferences: user.roleReferences,
    assignedFacilityReferences: user.assignedFacilityReferences,
  };
}

export function useBusinessSnapshot() {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  return useQuery({
    queryKey: ['business', 'snapshot', env, demo],
    queryFn: async (): Promise<BusinessSnapshot> => {
      if (demo) {
        return demoBusinessSnapshot();
      }
      const r = await hubFetch<BusinessOverviewResponse>('/api/business/overview');
      return toSnapshot(r);
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useOrgs() {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  return useQuery({
    queryKey: ['business', 'orgs', env, demo],
    queryFn: async (): Promise<{ items: OrganizationRow[]; isCapped: boolean }> => {
      if (demo) {
        return { items: demoOrgs(), isCapped: false };
      }
      return fetchAllPages<OrganizationRow>('/api/directory/organizations');
    },
    staleTime: 5 * 60_000,
  });
}

export function useFacilities() {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  return useQuery({
    queryKey: ['business', 'facilities', env, demo],
    queryFn: async (): Promise<{ items: FacilityRow[]; isCapped: boolean }> => {
      if (demo) {
        return { items: demoFacilities(), isCapped: false };
      }
      return fetchAllPages<FacilityRow>('/api/directory/facilities');
    },
    staleTime: 5 * 60_000,
  });
}

export function useOrg(id: string | null) {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  return useQuery({
    queryKey: ['business', 'org', id, env, demo],
    queryFn: async (): Promise<OrganizationDetail> => {
      if (id === null) throw new Error('id is required');
      if (demo) return demoOrg(id);
      return hubFetch<OrganizationDetail>(`/api/directory/organizations/${encodeURIComponent(id)}`);
    },
    enabled: id !== null,
    staleTime: 2 * 60_000,
  });
}

export function useFacility(id: string | null, orgId?: string | null) {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  return useQuery({
    queryKey: ['business', 'facility', id, orgId ?? null, env, demo],
    queryFn: async (): Promise<FacilityDetail> => {
      if (id === null) throw new Error('id is required');
      if (demo) return demoFacility(id);
      return hubFetch<FacilityDetail>(`/api/directory/facilities/${encodeURIComponent(id)}`, {
        searchParams: orgId ? { organizationId: orgId } : undefined,
      });
    },
    enabled: id !== null,
    staleTime: 2 * 60_000,
  });
}

export function useOrgUsers(orgId: string | null, query = '', enabled = true) {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  const effectiveQ = query.trim().length >= 2 ? query.trim() : '';
  return useQuery({
    queryKey: ['business', 'org-users', orgId, env, demo, effectiveQ],
    queryFn: async (): Promise<UsersQueryResult<DirectoryUserWithRoles>> => {
      if (orgId === null) throw new Error('orgId is required');
      if (demo) {
        const items = demoOrgUsers(orgId).filter((user) => {
          if (!effectiveQ) {
            return true;
          }

          const lower = effectiveQ.toLowerCase();
          return user.displayName.toLowerCase().includes(lower)
            || user.emailAddress.toLowerCase().includes(lower)
            || user.roles.some((role) => role.toLowerCase().includes(lower))
            || user.assignedFacilities.some((facility) => facility.toLowerCase().includes(lower));
        });
        return {
          items,
          continuation: null,
          isCapped: false,
          endpointUnavailable: false,
        };
      }
      const result = await fetchUsersWithFallback<DirectoryApiUser>(
        `/api/directory/organizations/${encodeURIComponent(orgId)}/users`,
        effectiveQ ? { q: effectiveQ } : undefined,
      );
      return {
        ...result,
        items: result.items.map(toDirectoryUserWithRoles),
      };
    },
    enabled: orgId !== null && enabled,
    staleTime: 2 * 60_000,
  });
}

export function useFacilityUsers(facilityId: string | null, orgId: string | null, query = '', enabled = true) {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  const effectiveQ = query.trim().length >= 2 ? query.trim() : '';
  return useQuery({
    queryKey: ['business', 'facility-users', facilityId, orgId, env, demo, effectiveQ],
    queryFn: async (): Promise<UsersQueryResult<DirectoryUserWithRoles>> => {
      if (facilityId === null) throw new Error('facilityId is required');
      if (orgId === null) throw new Error('orgId is required');
      if (demo) {
        const items = demoFacilityUsers(facilityId).filter((user) => {
          if (!effectiveQ) {
            return true;
          }

          const lower = effectiveQ.toLowerCase();
          return user.displayName.toLowerCase().includes(lower)
            || user.emailAddress.toLowerCase().includes(lower)
            || user.roles.some((role) => role.toLowerCase().includes(lower))
            || user.assignedFacilities.some((facility) => facility.toLowerCase().includes(lower));
        });
        return {
          items,
          continuation: null,
          isCapped: false,
          endpointUnavailable: false,
        };
      }
      const result = await fetchUsersWithFallback<DirectoryApiUser>(`/api/directory/facilities/${encodeURIComponent(facilityId)}/users`, {
        organizationId: orgId,
        ...(effectiveQ ? { q: effectiveQ } : {}),
      });
      return {
        ...result,
        items: result.items.map(toDirectoryUserWithRoles),
      };
    },
    enabled: facilityId !== null && orgId !== null && enabled,
    staleTime: 2 * 60_000,
  });
}

export function useUsers(query = '') {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  // Normalize short queries to '' so single-character inputs share the same
  // cache entry and queryFn result as the unfiltered fetch, avoiding
  // redundant network requests and RU cost on every keystroke.
  const effectiveQ = query.trim().length >= 2 ? query.trim() : '';
  return useQuery({
    queryKey: ['business', 'users', env, demo, effectiveQ],
    queryFn: async (): Promise<{ items: UserRow[]; isCapped: boolean }> => {
      if (demo) {
        const all = demoUsers();
        if (effectiveQ.length < 2) return { items: all, isCapped: false };
        const lower = effectiveQ.toLowerCase();
        const filtered = all.filter(
          (u) =>
            u.displayName.toLowerCase().includes(lower) ||
            u.emailAddress.toLowerCase().includes(lower),
        );
        return { items: filtered, isCapped: false };
      }
      const result = await fetchAllPages<UserRow>('/api/directory/users', effectiveQ ? { q: effectiveQ } : undefined);
      return result;
    },
    staleTime: 5 * 60_000,
  });
}
